// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { Component, type ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'

/**
 * D-UI-3 / E-07 — a render failure inside one part of a page shows an error card with Retry there, instead of
 * React unmounting the whole tree (a white page with nothing to act on). Resets when `resetKey` changes (a new
 * section or month), and Retry clears the error and calls `onRetry` (usually the query's refetch).
 * The error is also reported to the console so it is never silent.
 */
interface Props {
  /** What failed, in the reader's words ("this section", "Profitability v2"). */
  what: string
  children: ReactNode
  resetKey?: unknown
  onRetry?: () => void
}

interface State { error: Error | null }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State { return { error } }

  componentDidCatch(error: Error) { console.error(`[ErrorBoundary] ${this.props.what}:`, error) }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <Alert severity="error" role="alert" sx={{ my: 1 }}
        action={<Button size="small" color="inherit" onClick={() => { this.setState({ error: null }); this.props.onRetry?.() }}>Retry</Button>}>
        Could not show {this.props.what} — {error.message}. The rest of the page still works.
      </Alert>
    )
  }
}
